import { IMergeToken } from '../confirmation-template-editor/types';
import { ITemplate } from '../confirmation-template-editor/types';
import { IContractTemplate } from '../contract-template-editor/types';

export enum ContractStatus {
  draft = 'Draft',
  created = 'Created',
  published = 'Published',
  publishedVoid = 'Published - Void',
  signed = 'Signed',
  pending = 'Pending',
  requestContract = 'Request Contract',
  okToSend = 'OK to Send',
  sent = 'Sent',
  /** AMIE Classic placement contract status (legacy API string). */
  sentByTcc = 'Sent by TCC',
}

/** Used by client confirmation and other flows that still use legacy API strings. */
export enum DocSource {
  web = 'Web',
  classic = 'Classic',
}

/** Placement candidate contract `/contracts` API `placementContractDetailsItems[].source`. */
export enum ContractDocSource {
  amieWeb = 'AMIE Web',
  amieClassic = 'AMIE Classic',
}

/** Past snapshot from `GET /contracts` (`placementContractDetailsItems[].versions`). */
export interface IPlacementContractVersion {
  id: number;
  enteredByUserId: number;
  updatedByName: string;
  dateEntered: string;
  templateText: string;
  templateHtml?: string;
  /** Prior revision HTML for history preview / restore. */
  templateHtmlOld?: string;
}

export interface IContractDoc {
  id: string;
  documentId: string;
  documentImageId: string;
  source: ContractDocSource;
  version: string;
  fileName: string;
  status: ContractStatus;
  templateText: string;
  /** Rich HTML for TinyMCE; prefer over `templateText` when present (e.g. `templateText` may be RTF from API). */
  templateHtml?: string;
  /** Historical snapshots for Pending / Request Contract AMIE Web drafts (optional). */
  versions?: IPlacementContractVersion[];
  /**
   * Original RTF from GET contracts (`templateText`) when the editor loads HTML; used to repair
   * `{\\pictundefined …}` blobs after `htmlToRtf` by substituting `<<IMG:…>>` tokens in order.
   */
  preservedApiTemplateTextRtf?: string;
  isDraftManualEdit?: boolean;
  lastUpdatedById?: number | null;
  lastUpdatedByName?: string | null;
  lastUpdatedAt?: string | null;
  isDraft: boolean;

  /** Draft generated */
  draftCreatedBy: number;
  draftCreatedByName: string;
  draftCreatedOn: string;

  /** Draft updated */
  draftUpdateBy: number;
  draftUpdateByName: string;
  draftUpdateOn: string;

  /** Doc  created */
  docCreatedBy: number | null;
  docCreatedByName: string;
  docCreatedOn: string;

  /** Publish created */
  publishedBy: null;
  publishedByName: string;
  publishedOn: string;

  /** Signed created */
  signedBy: null;
  signedByName: string;
  signedOn: string;

  /** Void created */
  voidedBy: null;
  voidedByName: string;
  voidedOn: string;

  /** Account for all changes */
  updatedBy: number;
  updatedByName: string;
  updatedOn: string;

  /** Doc sent */
  sentBy: number;
  sentByName: string;
  sentDate: string;

  /** When true, contract is visible in TSC (Traveler Service Center). */
  showInTsc?: boolean;

  templateName: string;
  view: {
    draftGeneratedDate: string;
    docCreatedDate: string;
    cardUpdateDateString: string;
    updateDate: string;
    publishedDate: string;
    voidedDate: string;
    signedDate: string;
  };
}

export interface IPlacementCandidateContractDocEditor {
  templates: IContractDoc[];
  selectedTemplate: IContractDoc | null;
  /** Master contract templates from `GET …/document-template/get/new` (Settings-style library in the Templates sidebar). */
  catalogTemplates: IContractTemplate[];
  mergeTokenList: { [key: string]: { label: string; tokens: IMergeToken[] } };
  miscellaneous: {
    forceModalCloseFlag?: boolean;
  };
  classicDraftExists: boolean;
  amieWebDraftExists: boolean;
  amieWebCreatedExists: boolean;
  /** Doc Specs - start */
  document?: { data: string; version: number; fileName?: string; documentId?: string };
  renderPreviewScreen?: boolean;
  renderPdfView?: boolean;
  /** True when PDF bytes were generated in-browser from TinyMCE; false for API/sidebar PDF. */
  pdfViewFromEditor?: boolean;
  /** Doc Specs - end */
  /** One-shot HTML from `template-rows` API; consumed by TemplateEditor TinyMCE `insertContent` at cursor. */
  pendingTinyMceHtmlInsert: string | null;
}

export interface IContractDocPayload {
  placementId: number;
  template: ITemplate;
}

/** Response from POST `placement/contract-template/draft`. */
export interface IPlacementContractTemplateDraftResponse {
  placementId: number;
  templateText?: string;
  templateHtml?: string;
  TemplateHtml?: string;
  TemplateText?: string;
  documentId: number;
  placementContractTypeId: number;
  addendumOrdinalNumber?: number | null;
  /** Present when the API mirrors the legacy generate contract shape. */
  id?: string | null;
}

/** AMIE Web contracts in Draft or Pending are edited in the HTML template editor. */
export function isAmieWebEditableDraft(doc: IContractDoc | null | undefined): boolean {
  return (
    !!doc &&
    doc.source === ContractDocSource.amieWeb &&
    (doc.status === ContractStatus.draft || doc.status === ContractStatus.pending)
  );
}

/** 8-4-4-4-12 hex UUID (case-insensitive), as returned for placement document image ids. */
const PLACEMENT_DOCUMENT_IMAGE_GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidPlacementContractDocumentImageGuid(id: string | null | undefined): boolean {
  if (id == null) return false;
  return PLACEMENT_DOCUMENT_IMAGE_GUID_RE.test(String(id).trim());
}

/** AMIE Web row with a valid `documentImageId` GUID loads PDF (any status); otherwise editor when draft/pending/request. */
export function isAmieWebWithDocumentImage(doc: IContractDoc | null | undefined): boolean {
  return !!doc && doc.source === ContractDocSource.amieWeb && isValidPlacementContractDocumentImageGuid(doc.documentImageId);
}

/** Load PDF via `GET …/document/documentType/Contract/{id}` (any source). */
export function isPlacementContractPdfByStatus(doc: IContractDoc | null | undefined): boolean {
  if (!doc) return false;
  return doc.status === ContractStatus.okToSend || doc.status === ContractStatus.sentByTcc;
}

/** Numeric contract id for the Contract document-type endpoint. */
export function resolvePlacementContractDocumentTypeId(doc: IContractDoc | null | undefined): string | null {
  if (!doc) return null;
  const raw = doc.documentId != null ? String(doc.documentId).trim() : '';
  if (!raw) return null;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(n);
}

/**
 * AMIE Web: HTML editor for Draft, Pending, Request Contract (selection by status, not `documentImageId`).
 */
export function isAmieWebContractHtmlEditorByStatus(doc: IContractDoc | null | undefined): boolean {
  return (
    !!doc &&
    doc.source === ContractDocSource.amieWeb &&
    (doc.status === ContractStatus.draft ||
      doc.status === ContractStatus.pending ||
      doc.status === ContractStatus.requestContract)
  );
}

/**
 * Select list / URL: open merge HTML editor instead of fetching sidebar PDF (any `source`).
 */
export function opensInCandidateContractHtmlEditor(doc: IContractDoc | null | undefined): boolean {
  if (!doc) return false;
  return (
    doc.status === ContractStatus.draft ||
    doc.status === ContractStatus.pending ||
    doc.status === ContractStatus.requestContract
  );
}

/** Pending / Request with no `templateHtml`: editor must not load RTF from `templateText`; show regenerate placeholder instead. */
export function isPendingOrRequestContractWithoutHtml(doc: IContractDoc | null | undefined): boolean {
  if (!doc) return false;
  const noHtml = !doc.templateHtml || String(doc.templateHtml).trim() === '';
  return (
    noHtml &&
    (doc.status === ContractStatus.pending || doc.status === ContractStatus.requestContract)
  );
}

/** Pending / Request Contract: version-history control (enabled when `hasPlacementContractVersions`). */
export function isCandidateContractVersionHistoryEligible(doc: IContractDoc | null | undefined): boolean {
  if (!doc) return false;
  return doc.status === ContractStatus.pending || doc.status === ContractStatus.requestContract;
}

export function hasPlacementContractVersions(doc: IContractDoc | null | undefined): boolean {
  return Array.isArray(doc?.versions) && doc.versions.length > 0;
}

/** True when the history icon should be active (eligible row + non-empty `versions`). */
export function showPlacementContractVersionHistory(doc: IContractDoc | null | undefined): boolean {
  return isCandidateContractVersionHistoryEligible(doc) && hasPlacementContractVersions(doc);
}

export function isContractSignedOnPresent(signedOn: string | null | undefined): boolean {
  return signedOn != null && String(signedOn).trim() !== '';
}

/** Sent by TCC + Show in TSC + signed → display as Signed on card/toolbar. */
export function getContractDocDisplayStatus(doc: IContractDoc | null | undefined): ContractStatus | string {
  if (!doc) return '';
  if (
    doc.status === ContractStatus.sentByTcc &&
    doc.showInTsc === true &&
    isContractSignedOnPresent(doc.signedOn)
  ) {
    return ContractStatus.signed;
  }
  return doc.status;
}

export function shouldShowTscUnpublishButton(doc: IContractDoc | null | undefined): boolean {
  return (
    !!doc &&
    doc.status === ContractStatus.sentByTcc &&
    !isContractSignedOnPresent(doc.signedOn) &&
    doc.showInTsc === true
  );
}

export function shouldShowTscPublishButton(doc: IContractDoc | null | undefined): boolean {
  return (
    !!doc &&
    doc.status === ContractStatus.sentByTcc &&
    !isContractSignedOnPresent(doc.signedOn) &&
    doc.showInTsc !== true
  );
}

